import {NavigationItem} from '../components/navigation/navigationConfig';
import {User} from '../../features/auth/models/User';
import {AuthorizationService} from '../../Services/authorization/AuthorizationService';

/**
 * Navigation utilities for permission-based filtering
 * Following clean architecture principles
 */

/**
 * Check if user has access to a navigation item
 */
function hasNavigationAccess (user: User | null, item: NavigationItem): boolean {
  if (!user) {
    return false;
  }

  // Admin users have access to all navigation items
  const authService = AuthorizationService.getInstance();
  const isAdmin = authService.isAdmin(user);

  if (isAdmin) {
    return true;
  }

  // No permission/role requirements - always accessible
  if (!item.permission && !item.permissions && !item.role && !item.roles) {
    return true;
  }

  // Check single permission
  if (item.permission) {
    return checkUserPermission(user, item.permission);
  }

  // Check multiple permissions
  if (item.permissions && item.permissions.length > 0) {
    if (item.requireAll) {
      // User must have ALL permissions
      return item.permissions.every(permission => checkUserPermission(user, permission));
    } else {
      // User needs ANY permission
      return item.permissions.some(permission => checkUserPermission(user, permission));
    }
  }

  // Check single role
  if (item.role) {
    return user.role.name === item.role;
  }

  // Check multiple roles
  if (item.roles && item.roles.length > 0) {
    if (item.requireAll) {
      // User must have ALL roles (not applicable for single user)
      return false;
    } else {
      // User needs ANY role
      return item.roles.includes(user.role.name);
    }
  }

  return false;
}

/**
 * Filter navigation items based on user permissions
 */
export function filterNavigationItems (
  items: NavigationItem[],
  user: User | null
): NavigationItem[] {
  const filtered = items
    .map(item => {
      // Check if user has access to this item
      const hasAccess = hasNavigationAccess(user, item);
      if (!hasAccess) {
        return null;
      }

      // If item has children, filter them as well
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterNavigationItems(item.children, user);

        // If no children are accessible, hide the parent item
        if (filteredChildren.length === 0) {
          return null;
        }

        // Return item with filtered children
        return {
          ...item,
          children: filteredChildren
        };
      }

      return item;
    })
    .filter((item): item is NavigationItem => item !== null);

  return filtered;
}

/**
 * Get visible navigation items count
 */
export function getVisibleNavigationCount (
  items: NavigationItem[],
  user: User | null
): number {
  const filteredItems = filterNavigationItems(items, user);

  const countItems = (items: NavigationItem[]): number => {
    return items.reduce((count, item) => {
      let itemCount = 1; // Count the item itself

      if (item.children && item.children.length > 0) {
        itemCount += countItems(item.children);
      }

      return count + itemCount;
    }, 0);
  };

  return countItems(filteredItems);
}

/**
 * Check if user has permission using AuthorizationService
 */
function checkUserPermission (user: User, permission: string): boolean {
  const authService = AuthorizationService.getInstance();
  return authService.hasPermission(user, permission);
}

/**
 * Get navigation breadcrumb for current path
 */
export function getNavigationBreadcrumb (
  items: NavigationItem[],
  currentPath: string,
  user: User | null
): NavigationItem[] {
  const breadcrumb: NavigationItem[] = [];

  const findPath = (items: NavigationItem[], targetPath: string, path: NavigationItem[] = []): NavigationItem[] | null => {
    for (const item of items) {
      const currentPath = [...path, item];

      if (item.path === targetPath) {
        return currentPath;
      }

      if (item.children && item.children.length > 0) {
        const found = findPath(item.children, targetPath, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const filteredItems = filterNavigationItems(items, user);
  const foundPath = findPath(filteredItems, currentPath);

  return foundPath || [];
}

/**
 * Check if navigation item is currently active
 */
export function isNavigationItemActive (
  item: NavigationItem,
  currentPath: string
): boolean {
  if (item.path === currentPath) {
    return true;
  }

  if (item.children && item.children.length > 0) {
    return item.children.some(child =>
      isNavigationItemActive(child, currentPath)
    );
  }

  return false;
}
